import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { FinanceProvider } from '@upstart/harmony/lib/legos/constants';
import {
  ReportFinanceApplication,
  UpstartApplicationStatus,
} from '@upstart/harmony/lib/legos/financingTypes';

import FinanceApplicationService from '../../services/FinanceApplicationService';
import LoanStatusList from '../../components/LoanStatusList';
import { UpstartLoanStatusHeader } from './UpstartLoanStatusHeader';
import { getStatus } from '../../components/LoanStatusList/LoanStatusList';

export const UpstartAllLoanStatus: React.FC = () => {
  const [startDate, setStartDate] = useState(moment().startOf('day'));
  const [endDate, setEndDate] = useState(moment().endOf('day'));
  const [searchTerm, setSearchTerm] = useState('');
  const [loanStatusList, setLoanStatusList] = useState<
    {
      financeApplication: ReportFinanceApplication;
      status: UpstartApplicationStatus;
    }[]
  >([]);

  const applyRange = (newRange: any) => {
    setStartDate(moment(newRange.startDate));
    setEndDate(moment(newRange.endDate));
  };

  const asyncApplications = useAsync(
    FinanceApplicationService.getAllApplications,
    [
      FinanceProvider.UPSTART,
      moment(startDate).toISOString(),
      moment(endDate).toISOString(),
    ],
  );
  const financeApplications = asyncApplications.result;

  const asyncApplicationStatuses = useAsyncCallback(
    FinanceApplicationService.getApplicationStatuses,
  );

  const getFinanceApplicationStatuses = async (ids: string[]) => {
    try {
      await asyncApplicationStatuses.execute(ids);
    } catch (e) {
      // do nothing
    }
  };

  useEffect(() => {
    if (financeApplications) {
      const ids = Array.from(
        new Set(
          financeApplications.map((item: any) => item.providerApplicationId),
        ),
      ) as string[];

      getFinanceApplicationStatuses(ids);
    }
  }, [financeApplications]);

  const findStatusFromApplicationId = (
    applicationId?: string,
    statuses?: UpstartApplicationStatus[],
  ) => (statuses || []).find(s => applicationId === s.applicationId) || {};

  const applicationStatuses = asyncApplicationStatuses.result;

  // handle creating loan status and searching
  useEffect(() => {
    const filteredLoanStatusList = [];

    for (const financeApplication of financeApplications || []) {
      const status = findStatusFromApplicationId(
        financeApplication.providerApplicationId,
        applicationStatuses || [],
      );

      const loanStatus = {
        financeApplication,
        status,
      };

      if (searchTerm) {
        const searchTermLC = searchTerm.toLowerCase();
        const { applicant } = financeApplication.deal;
        const { car } = financeApplication;
        const applicantName = `${applicant?.firstName} ${applicant?.lastName}`.toLowerCase();
        if (
          applicantName.includes(searchTermLC) ||
          (status?.contractNumber || '').toLowerCase().includes(searchTermLC) ||
          car.name?.toLowerCase().includes(searchTermLC) ||
          getStatus(financeApplication, status.title || '')
            .toLowerCase()
            .includes(searchTermLC)
        ) {
          filteredLoanStatusList.push(loanStatus);
        }
      } else {
        filteredLoanStatusList.push(loanStatus);
      }
    }

    setLoanStatusList(filteredLoanStatusList);
  }, [financeApplications, applicationStatuses, searchTerm]);

  const isFetching =
    asyncApplicationStatuses.loading || asyncApplications.loading;
  const isError = asyncApplicationStatuses.error || asyncApplications.error;

  return (
    <>
      <UpstartLoanStatusHeader
        startDate={startDate}
        endDate={endDate}
        applyRange={applyRange}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
      />

      <LoanStatusList
        loanStatusList={loanStatusList}
        error={isError}
        isFetching={isFetching}
        isAllLoanStatus
      />
    </>
  );
};
